// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import {
    BrandCategoriesCollection,
    BrandSolutionsCollection,
    SolutionParametersModel,
} from "@mcmc/App/Services/CustomBrandService";
import { Draft } from "immer";

export type BrandFiltersState = {
    brandCategories: BrandCategoriesCollection | null;
    brandSolutions: BrandSolutionsCollection | null;
    isLoadingBrandCategories: boolean;
    isLoadingBrandSolutions: boolean;
    selectedBrandViewType: string;
    selectedSolutionParameters: SolutionParametersModel | null;
};

const initialBrandFiltersState: BrandFiltersState = {
    brandCategories: null,
    brandSolutions: null,
    isLoadingBrandCategories: false,
    isLoadingBrandSolutions: false,
    // Default Value for the card type is the alphabetical order
    selectedBrandViewType: "alphabeticalSelect",
    selectedSolutionParameters: {
        sort: "name asc",
        select: "id,detailPagePath,name,logoSmallImagePath,logoAltText",
        pageSize: 500,
        page: 1,
        solution: "",
    },
};

const xBrandFilters = createTypedReducerWithImmer(initialBrandFiltersState, {
    "xBrandCategories/BeginLoadBrandCategories": (draft: Draft<BrandFiltersState>) => {
        draft.isLoadingBrandCategories = true;
    },
    "xBrandCategories/CompleteLoadBrandCategories": (
        draft: Draft<BrandFiltersState>,
        action: { brandCategories: BrandCategoriesCollection | null },
    ) => {
        draft.brandCategories = action.brandCategories;
        draft.isLoadingBrandCategories = false;
    },
    "xBrandSolutions/BeginLoadBrandSolutions": (draft: Draft<BrandFiltersState>) => {
        draft.isLoadingBrandSolutions = true;
    },
    "xBrandSolutions/CompleteLoadBrandSolutions": (
        draft: Draft<BrandFiltersState>,
        action: { brandSolutions: BrandSolutionsCollection | null },
    ) => {
        draft.brandSolutions = action.brandSolutions;
        draft.isLoadingBrandSolutions = false;
    },
    "xBrandCategories/SelectBrandViewType": (
        draft: Draft<BrandFiltersState>,
        action: { selectedBrandViewType: string },
    ) => {
        draft.selectedBrandViewType = action.selectedBrandViewType;
    },
    "xBrandSolutions/SelectedSolutionParameters": (
        draft: Draft<BrandFiltersState>,
        action: { selectedSolutionParameters: SolutionParametersModel | null },
    ) => {
        draft.selectedSolutionParameters = action.selectedSolutionParameters;
    },
});

(reducers as any).xBrandFilters = xBrandFilters;

type xBrandFiltersReducers = {
    BrandFilters: typeof xBrandFilters;
};

export type xBrandFiltersActions = Parameters<xBrandFiltersReducers[keyof xBrandFiltersReducers]>[1] | AnyAction;
